import type { PanelComponentKey } from './components.js';
import type { SelectionKind } from './store.js';

// Single source of truth for every panel the user can open via the
// command-palette catalogue. Adding a new panel = appending one entry
// here. The palette's empty state shows every entry whose
// `needsEntity` is falsy. The order here IS the display order.

export type PanelCategory = 'workflow' | 'discovery' | 'map' | 'morning-brief' | 'help';

export interface PanelDef {
  id: PanelComponentKey;
  title: string;
  category: PanelCategory;
  /** Default panel title-prefix when adding a new instance (otherwise
   *  copy of `title`). */
  shortTitle?: string;
  /** If true, this panel renders nothing useful without an entity
   *  param pre-bound — exclude from the empty-state catalogue. */
  needsEntity?: boolean;
  /** If true, this panel is independent of the link-group / channel
   *  system. The tab header skips the channel-colour picker and the
   *  linked/pinned toggle. Used for aggregate panels (no per-entity
   *  context) like the morning-brief stubs. */
  noChannel?: boolean;
  /** If true, the panel BROADCASTS selection to its linked channel but
   *  does not consume channel state — its own row highlight is sourced
   *  from local panel state, not the group's currentEntityId. Use this
   *  for the discovery surface (the Screener) so a click in a downstream
   *  panel (Watchlist, Company Overview) doesn't reach back and reset
   *  the discovery context the user was scrolling through. */
  broadcastOnly?: boolean;
  /** Which channel selection kinds this panel follows (TERMINAL_HOME_GRAIN
   *  step 2). Default ['operator'] — every pre-step-2 panel. Read through
   *  panelAccepts(). A panel keeps its last accepted selection when a
   *  selection of another kind arrives (see usePanelSelection). */
  accepts?: SelectionKind[];
}

export const PANEL_REGISTRY: ReadonlyArray<PanelDef> = [
  // Order here IS the command-palette display order. Names follow the
  // clinical/diagnosis metaphor (Triage → Record → Ward …).
  // --- discovery ----------------------------------------------------
  { id: 'screener', title: 'Triage', category: 'discovery', broadcastOnly: true },
  {
    id: 'origination-signal',
    title: 'Referrals',
    category: 'discovery',
    broadcastOnly: true,
  },
  { id: 'company-overview', title: 'Record', category: 'discovery' },
  // Care-home grain (TERMINAL_HOME_GRAIN_SPEC step 3). Homes screens individual
  // homes and broadcasts a home selection (which also moves a linked Record to
  // the home's operator); Home record follows home selections only.
  {
    id: 'homes',
    title: 'Homes',
    category: 'discovery',
    broadcastOnly: true,
    accepts: ['home'],
  },
  { id: 'home-record', title: 'Home record', category: 'discovery', accepts: ['home'] },
  { id: 'watchlist', title: 'Ward', category: 'discovery' },
  // Alerts — proactive monitoring of watchlisted companies. Broadcasts the
  // clicked company to a linked Record (broadcastOnly: a downstream selection
  // never reaches back and reorders the feed).
  { id: 'alerts', title: 'Alerts', category: 'discovery', broadcastOnly: true },

  // --- workflow -----------------------------------------------------
  { id: 'bolt-on', title: 'Catchment', category: 'workflow' },
  { id: 'census', title: 'Census', category: 'workflow' },
  {
    id: 'market-opportunity',
    title: 'Indications',
    category: 'workflow',
    noChannel: true,
  },
  {
    // The buyer's standing BD strategy — upstream of Prescription, shapes it.
    id: 'mandate',
    title: 'Mandate',
    category: 'workflow',
    noChannel: true,
  },
  {
    // What the buyer already operates — owned holdings (DB-linked or manual).
    // Attached to Prescription to exclude owned assets + seed the anchor.
    id: 'portfolio',
    title: 'Portfolio',
    category: 'workflow',
    noChannel: true,
  },
  {
    id: 'platform-builder',
    title: 'Prescription',
    category: 'workflow',
    // A channel panel: it broadcasts its constructed portfolio (entity set) over
    // the channel, so a linked Watchlist (Ward) panel renders the table. Needs
    // the channel picker to pair with that Ward panel.
  },
  {
    id: 'rollup-simulator',
    title: 'Roll-Up',
    category: 'workflow',
    // A channel panel (like Bolt-On), NOT noChannel: it broadcasts its combined
    // catchment over the channel, so it needs the channel picker to link with
    // the Map / Census panels.
  },
  {
    id: 'consolidators',
    title: 'Prevalence',
    category: 'workflow',
    noChannel: true,
  },
  {
    // A channel panel: broadcasts the focused economic group over group-link
    // (a linked Group Overview panel renders the standalone detail) and the
    // clicked operator's entity id (a linked Record panel opens the company).
    id: 'ownership-network',
    title: 'Ownership Explorer',
    category: 'workflow',
  },
  {
    // The standalone group record (Record-pattern, for GROUPS) — consumes the
    // group broadcast from an Ownership Network panel on the same channel.
    id: 'group-overview',
    title: 'Group',
    category: 'discovery',
  },
  {
    id: 'landing-pressure',
    title: 'Pressure',
    category: 'workflow',
    noChannel: true,
  },
  {
    // Demand Shifts v2 — population-projection demand outlook (Eurostat
    // EUROPOP2023 + ONS 2022-based). Replaces the retired news-agent stub.
    id: 'demand-shifts',
    title: 'Demand',
    category: 'workflow',
    noChannel: true,
  },

  // Map sits in its own category, just above Help.
  // Follows every selection kind and highlights whatever is selected.
  {
    id: 'map',
    title: 'Map',
    category: 'map',
    accepts: ['home', 'operator', 'group', 'landlord'],
  },

  // --- help / Reference ---------------------------------------------
  // (the 'help' category renders as "Reference" in the command palette).
  { id: 'coverage', title: 'Coverage', category: 'help', noChannel: true },
  { id: 'methodology', title: 'Methodology', category: 'help', noChannel: true },
  { id: 'sources', title: 'Sources', category: 'help', noChannel: true },
  { id: 'help', title: 'Help', category: 'help' },

  // Removed from the catalogue (UI) but kept registered in components.tsx so
  // saved layouts don't crash, and their data/logic remain available to other
  // surfaces: reimbursement (content moved into Indications, country altitude),
  // landing-alerts, landing-watchlist-changes, landing-buyer (M&A tracker).
];

export function findPanelDef(id: PanelComponentKey): PanelDef | undefined {
  return PANEL_REGISTRY.find((p) => p.id === id);
}

const NO_CHANNEL_COMPONENTS = new Set(
  PANEL_REGISTRY.filter((p) => p.noChannel).map((p) => p.id as string),
);

/** True if the given panel-component-key opts out of the channel /
 *  link-group system entirely. */
export function isChannellessPanel(component: string): boolean {
  return NO_CHANNEL_COMPONENTS.has(component);
}

const BROADCAST_ONLY_COMPONENTS = new Set(
  PANEL_REGISTRY.filter((p) => p.broadcastOnly).map((p) => p.id as string),
);

/** True if this panel writes to its linked channel but doesn't follow it
 *  back — its row highlight comes from a local panel-scoped selection
 *  rather than the group's currentEntityId. See PanelDef.broadcastOnly. */
export function isBroadcastOnlyPanel(component: string): boolean {
  return BROADCAST_ONLY_COMPONENTS.has(component);
}

const DEFAULT_ACCEPTS: readonly SelectionKind[] = ['operator'];

/** The selection kinds a panel follows — PanelDef.accepts, else
 *  ['operator']. Returns a stable array (safe to pass to usePanelSelection). */
export function panelAccepts(id: string): readonly SelectionKind[] {
  return PANEL_REGISTRY.find((p) => p.id === id)?.accepts ?? DEFAULT_ACCEPTS;
}
